'use client';

import { useState } from 'react';
import { Container, Title, Text, TextInput, Button, Paper, Stack, Alert, Group, Box } from '@mantine/core';
import { IconSend, IconCheck, IconAlertCircle, IconPackage } from '@tabler/icons-react';
import { PageHero } from '@/components/PageHero';
import api from '@/lib/api';
import { trackEvent } from '@/lib/telemetry';

export default function RequestSetPage() {
  const [setNumber, setSetNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!setNumber.trim()) {
      setError('Please enter a LEGO set number');
      trackEvent('Set Request Failed', { reason: 'validation' });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.requestSet(setNumber.trim(), email.trim() || null);
      setSuccess(true);
      setSetNumber('');
      setEmail('');
      trackEvent('Set Request Succeeded');
    } catch (err) {
      setError(err.message || 'Failed to submit request. Please try again.');
      trackEvent('Set Request Failed', { reason: 'request_error' });
    } finally {
      setLoading(false);
    }
  };

  const handleNewRequest = () => {
    setSuccess(false);
    setError(null);
  };

  return (
    <>
      <PageHero
        icon={IconPackage}
        title="Request a LEGO Set"
        subtitle="Can't find your set? Request it and we'll add it within a few days!"
      />
      <Container size="sm" py="xl">
      <Stack gap="xl">
        {/* Success State */}
        {success ? (
          <Paper
            p="xl"
            radius="lg"
            style={{
              backgroundColor: '#d3f9d8',
              border: '1px solid #b2f2bb',
              textAlign: 'center',
            }}
          >
            <IconCheck size={48} color="#2f9e44" style={{ marginBottom: 16 }} />
            <Title order={3} mb="xs" style={{ color: '#2f9e44' }}>
              Request Submitted!
            </Title>
            <Text c="dimmed" mb="lg">
              Thank you for your request. We'll add your set within a few days.
            </Text>
            <Button
              variant="light"
              color="green"
              onClick={handleNewRequest}
              radius="xl"
            >
              Request Another Set
            </Button>
          </Paper>
        ) : (
          /* Request Form */
          <Paper
            p="xl"
            radius="lg"
            style={{
              backgroundColor: '#fff',
              border: '1px solid #e9ecef',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            }}
          >
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                {error && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    variant="light"
                    radius="md"
                  >
                    {error}
                  </Alert>
                )}

                <TextInput
                  label="LEGO Set Number"
                  placeholder="e.g. 75192, 10294, 42151"
                  size="md"
                  radius="md"
                  required
                  value={setNumber}
                  onChange={(e) => setSetNumber(e.currentTarget.value)}
                  classNames={{ input: 'form-input' }}
                />

                <TextInput
                  label="Email (optional)"
                  description="Get notified when your set is added"
                  placeholder="your@email.com"
                  size="md"
                  radius="md"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  classNames={{ input: 'form-input' }}
                />

                <Button
                  type="submit"
                  size="lg"
                  radius="xl"
                  loading={loading}
                  leftSection={<IconSend size={18} />}
                  style={{
                    marginTop: 8,
                    boxShadow: '0 4px 12px rgba(34, 139, 230, 0.3)',
                  }}
                >
                  Submit Request
                </Button>
              </Stack>
            </form>
          </Paper>
        )}

        {/* Info Section */}
        <Paper
          p="lg"
          radius="lg"
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
          }}
        >
          <Title order={4} mb="sm" style={{ color: '#495057' }}>
            How it works
          </Title>
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              <Text component="span" fw={600} c="dark">1.</Text> Enter the official LEGO set number (found on the box)
            </Text>
            <Text size="sm" c="dimmed">
              <Text component="span" fw={600} c="dark">2.</Text> We process the instruction PDF and match all parts
            </Text>
            <Text size="sm" c="dimmed">
              <Text component="span" fw={600} c="dark">3.</Text> Your set becomes available within a few days
            </Text>
          </Stack>
        </Paper>
      </Stack>
      </Container>
    </>
  );
}
